import React, { createContext, useContext, useEffect, useState } from 'react';
import { ChainInfo, Keplr } from '@keplr-wallet/types';
import { EncodeObject, OfflineSigner } from '@cosmjs/proto-signing';
import { SigningCosmWasmClient } from '@cosmjs/cosmwasm-stargate';
type WalletContextType = {
    cosmosWalletAddress: string;
    keplrWalletAddress: string | null;
    isConnected: boolean;
    isError: boolean;
    reconnect: () => Promise<void>;
    connectKeplrWallet: () => Promise<void>;
    connectCosmosWallet: () => Promise<void>;
    disconnectKeplrWallet: () => Promise<void>;
    disconnectCosmosWallet: () => Promise<void>;
    getTokenBalanceKeplr: (tokenDenom: string) => Promise<string>;
    getTokenBalanceCosmos: (tokenDenom: string) => Promise<string>;
    executeSmartContract: (
        senderAddress: string,
        contractAddress: string,
        msg: Record<string, unknown>,
        fee: 'auto',
    ) => Promise<any>;
};

export const WalletContext = createContext<WalletContextType>({
    cosmosWalletAddress: '',
    keplrWalletAddress: '',
    isConnected: false,
    isError: false,
    reconnect: () => {
        return new Promise(() => console.log('reconnect'));
    },
    connectKeplrWallet: () => {
        return new Promise(() => {
            console.log('connectWallet');
        });
    },
    connectCosmosWallet: () => {
        return new Promise(() => {
            console.log('connectCosmosWallet');
        });
    },
    disconnectCosmosWallet: () => {
        return new Promise(() => {
            console.log('disconnectCosmosWallet');
        });
    },
    disconnectKeplrWallet: () => {
        return new Promise(() => {
            console.log('disconnectKeplrWallet');
        });
    },
    getTokenBalanceKeplr: () => {
        return new Promise(() => {
            console.log('getTokenBalanceKeplr');
        });
    },
    getTokenBalanceCosmos: () => {
        return new Promise(() => {
            console.log('getTokenBalanceCosmos');
        });
    },
    executeSmartContract: () => {
        return new Promise(() => {
            console.log('executeSmartContract');
        });
    },
});

interface WalletProviderProps {
    cosmostationClient: CosmostationClient;
    keplrClient: KeplrClient;
}

export const WalletProvider: React.FC<
    React.PropsWithChildren<WalletProviderProps>
> = (props) => {
    const [keplrWalletAddress, setKeplrWalletAddress] = useState<string | null>(
        null,
    );
    const [cosmosWalletAddress, setCosmosWalletAddress] = useState<string>('');
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    useEffect(() => {
        reconnect();
    }, []);

    const connectKeplrAccount = async () => {
        const keplrAccounts = await props.keplrClient.getAccounts();
        console.log(keplrAccounts);
        if (!keplrAccounts || !keplrAccounts[0]) {
            console.error('No keplr wallets found');
        }
        setKeplrWalletAddress(keplrAccounts?.[0]?.address ?? null);
    };
    const connectCosmosAccount = async () => {
        const cosmosAccounts = await props.cosmostationClient.getAccounts();
        setCosmosWalletAddress(cosmosAccounts?.[0]?.address ?? '');
    };
    const disconnectKeplrWallet = async () => {
        setKeplrWalletAddress(null);
    };
    const disconnectCosmosWallet = async () => {
        setCosmosWalletAddress('');
    };
    const getTokenBalanceKeplr = async (
        tokenDenom: string,
    ): Promise<string> => {
        return await props.keplrClient.getTokenBalance(
            keplrWalletAddress ?? '',
            tokenDenom,
        );
    };
    const getTokenBalanceCosmos = async (
        tokenDenom: string,
    ): Promise<string> => {
        return await props.cosmostationClient.getTokenBalance(
            cosmosWalletAddress,
            tokenDenom,
        );
    };
    const reconnect = async () => {
        await props.keplrClient
            .init()
            .then(() => {
                setIsConnected(true);
                setIsError(false);
            })
            .catch(() => {
                setIsConnected(false);
                setIsError(true);
            });
    };
    const executeSmartContract = async (
        senderAddress: string,
        contractAddress: string,
        msg: Record<string, unknown>,
        fee: 'auto',
    ) => {
        if (!contractAddress || !msg) {
            throw new Error(
                'Invalid parameters: contractAddress and msg must be provided',
            );
        }
        return props.keplrClient.executeSmartContract(
            senderAddress,
            contractAddress,
            msg,
            fee,
        );
    };

    return (
        <WalletContext.Provider
            value={{
                isConnected: isConnected,
                isError: isError,
                reconnect: reconnect,
                cosmosWalletAddress: cosmosWalletAddress,
                keplrWalletAddress: keplrWalletAddress,
                connectKeplrWallet: connectKeplrAccount,
                connectCosmosWallet: connectCosmosAccount,
                disconnectCosmosWallet: disconnectCosmosWallet,
                disconnectKeplrWallet: disconnectKeplrWallet,
                getTokenBalanceKeplr: getTokenBalanceKeplr,
                getTokenBalanceCosmos: getTokenBalanceCosmos,
                executeSmartContract: executeSmartContract,
            }}
        >
            {props.children}
        </WalletContext.Provider>
    );
};

export function useWalletContext() {
    return useContext(WalletContext);
}

const bluechip_chain_id = 'tokenfactory';//import.meta.env.VITE_BLUECHIP_CHAIN_ID;

export const getChainInfo = (): ChainInfo => ({
    chainId: bluechip_chain_id,
    chainName: bluechip_chain_id.toUpperCase() + " Network",
    rpc: 'http://127.0.0.1:9090',
    rest: 'http://127.0.0.1:9091',
    stakeCurrency: {
        coinDenom: 'stake'.toUpperCase(),
        coinMinimalDenom: 'stake',
        coinDecimals: 0,
    },
    bip44: {
        coinType: 118
    },
    bech32Config: {
        bech32PrefixAccAddr: 'cosmos',
        bech32PrefixAccPub: 'cosmos' + "pub",
        bech32PrefixValAddr: 'cosmos' + "valoper",
        bech32PrefixValPub: 'cosmos' + "valoperpub",
        bech32PrefixConsAddr: 'cosmos' + "valcons",
        bech32PrefixConsPub: 'cosmos' + "valconspub",
    },
    currencies: [
    {
      denom: "stake",
      amount: "400053176"
    },
    {
      denom: "token",
      amount: "40000"
    }
  ].map((x) => {
    const y = {
            coinDenom: x.denom?.toUpperCase() ?? "",
            coinMinimalDenom: x.denom ?? "",
            coinDecimals: 0,
          };
    return y;
  }),
    feeCurrencies:[
    {
      denom: "stake",
      amount: "400053176"
    },
    {
      denom: "token",
      amount: "40000"
    }
  ].map((x) => {
          const y = {
            coinDenom: x.denom?.toUpperCase() ?? "",
            coinMinimalDenom: x.denom ?? "",
            coinDecimals: 0,
          };
          return y;
        }),
})

export class KeplrClient {
    private _offlineSigner?: OfflineSigner = undefined;
    private _client?: SigningCosmWasmClient = undefined;
    private _chainId: string;
    private _rpcEndpoint: string;
    private _keplr: any;
    constructor(
        bluechipChainId: string,
        bluechipRpcEndpoint: string,
        keplr: any,
    ) {
        this._chainId = bluechipChainId;
        this._rpcEndpoint = bluechipRpcEndpoint;
        this._keplr = keplr;
    }
    public async init() {
        if (this._keplr) {
            try {
                await this._keplr.experimentalSuggestChain(getChainInfo());
                await this._keplr.enable(this._chainId);
                this._offlineSigner = this._keplr.getOfflineSigner(
                    this._chainId,
                );
                if (this._offlineSigner) {
                    this._client =
                        await SigningCosmWasmClient.connectWithSigner(
                            this._rpcEndpoint,
                            this._offlineSigner,
                        );
                }
            } catch (error) {
                console.error('Unable to initialize keplr client', error);
            }
        } else {
            console.error('Enable keplr extension to support wallet');
        }
    }

    public async getAccounts() {
        const accounts = (await this._offlineSigner?.getAccounts()) ?? [];
        return accounts;
    }
    public async getTokenBalance(
        address: string,
        tokenContractAddress: string,
    ): Promise<string> {
        try {
            const query = { balance: { address } };
            const response = await this._client?.queryContractSmart(
                tokenContractAddress,
                query,
            );
            return response?.balance ?? '0';
        } catch (error) {
            console.error('Failed to get token balance:', error);
            return '0';
        }
    }
    public async executeSmartContract(
        senderAddress: string,
        contractAddress: string,
        msg: Record<string, unknown>,
        fee: 'auto',
    ): Promise<any> {
        if (!senderAddress || !contractAddress || !msg) {
            throw new Error(
                'Invalid parameters provided to executeSmartContract',
            );
        }
        const fixedFee = {
            amount: [{ denom: 'ubluechip', amount: '500' }],
            gas: '200000',
        };
        if (!this._client) {
            throw new Error('Client has not been initialized.');
        }
        try {
            console.log('Executing contract:', {
                senderAddress,
                contractAddress,
                msg,
                fixedFee,
            });
            await this._client.execute(
                senderAddress,
                contractAddress,
                msg,
                fixedFee,
            );
        } catch (error) {
            console.error('Contract execution failed:', error);
            throw error;
        }
    }
}

export class CosmostationClient {
    private _offlineSigner?: OfflineSigner = undefined;
    private _client?: SigningCosmWasmClient = undefined;
    constructor(chainId: string, rpcEndpoint: string, cosmostation?: Keplr) {
        cosmostation?.enable(chainId).then(() => {
            this._offlineSigner = cosmostation.getOfflineSigner(chainId);
            SigningCosmWasmClient.connectWithSigner(
                rpcEndpoint,
                this._offlineSigner,
            ).then((signer) => (this._client = signer));
        });
    }

    public async getAccounts() {
        return (await this._offlineSigner?.getAccounts()) ?? [];
    }
    public async getTokenBalance(
        address: string,
        tokenContractAddress: string,
    ): Promise<string> {
        try {
            const query = { balance: { address } };
            const response = await this._client?.queryContractSmart(
                tokenContractAddress,
                query,
            );
            return response.balance || '0';
        } catch (error) {
            console.error('Failed to get token balance:', error);
            return '0';
        }
    }
    public async executeSmartContract(
        senderAddress: string,
        contractAddress: string,
        msg: Record<string, unknown>,
        fee: 'auto',
    ): Promise<any> {
        if (!senderAddress || !contractAddress || !msg) {
            throw new Error(
                'Invalid parameters provided to executeSmartContract',
            );
        }

        return this._client?.execute(senderAddress, contractAddress, msg, fee);
    }
}
